import { AuditLog } from "../models/service/audit-log.model";
import { UserAuditLogModel } from "../models/data/user-audit-log.model";
import { MemberAuditLogModel } from "../models/data/member-audit-log.model";
import { OrganizationAuditLogModel } from "../models/data/organization-audit-log.model";
import { PropertyAuditLogModel } from "../models/data/property-audit-log.model";
import { PropertyChargeScheduleAuditLogModel } from "../models/data/property-charge-schedule-audit-log.model";
import { PropertyChargeAuditLogModel } from "../models/data/property-charge-audit-log.model";
import { BudgetAuditLogModel } from "../models/data/budget-audit-log.model";
import { DocumentCenterAuditLogModel } from "../models/data/document-center-audit-log.model";
import { ChartOfAccountAuditLogModel } from "../models/data/chart-of-account-audit-log.model";
import { BankTransactionAuditLogModel } from "../models/data/bank-transaction-audit-log.model";
import { AuditEntityType } from "../enums/base.enums";

export class AuditLogMapper {
    /**
     * Maps a UserAuditLogResponse to the generic AuditLog model
     * @param response The user audit log response from the API
     * @returns A mapped AuditLog instance
     */
    static mapUserAuditLog(response: UserAuditLogModel): AuditLog {
        return {
            entityId: response.userId,
            actingEntityId: response.actingUserId ?? 0,
            actingEntityName: response.actingUserName ?? '',
            action: response.action,
            actionTimestamp: response.actionTimestamp,
            note: response.note ?? undefined,
            entityType: AuditEntityType.User
        };
    }

    /**
     * Maps an array of UserAuditLogResponse to an array of generic AuditLog models
     * @param responses Array of user audit log responses from the API
     * @returns Array of mapped AuditLog instances
     */
    static mapUserAuditLogs(responses: UserAuditLogModel[]): AuditLog[] {
        return responses.map(response => this.mapUserAuditLog(response));
    }

    /**
     * Maps a MemberAuditLogResponse to the generic AuditLog model
     * @param response The member audit log response from the API
     * @returns A mapped AuditLog instance
     */
    static mapMemberAuditLog(response: MemberAuditLogModel): AuditLog {
        return {
            entityId: response.memberId,
            actingEntityId: response.actingUserId ?? 0,
            actingEntityName: response.actingUserName ?? '',
            action: response.action,
            actionTimestamp: response.actionTimestamp,
            note: response.note ?? undefined,
            entityType: AuditEntityType.Member
        };
    }

    /**
     * Maps an array of MemberAuditLogResponse to an array of generic AuditLog models
     * @param responses Array of member audit log responses from the API
     * @returns Array of mapped AuditLog instances
     */
    static mapMemberAuditLogs(responses: MemberAuditLogModel[]): AuditLog[] {
        return responses.map(response => this.mapMemberAuditLog(response));
    }

    /**
     * Maps an OrganizationAuditLogResponse to the generic AuditLog model
     * @param response The organization audit log response from the API
     * @returns A mapped AuditLog instance
     */
    static mapOrganizationAuditLog(response: OrganizationAuditLogModel): AuditLog {
        return {
            entityId: response.organizationId,
            actingEntityId: response.actingUserId ?? 0,
            actingEntityName: response.actingUserName ?? '',
            action: response.action,
            actionTimestamp: response.actionTimestamp,
            note: response.note ?? undefined,
            entityType: AuditEntityType.Organization
        };
    }

    /**
     * Maps an array of OrganizationAuditLogResponse to an array of generic AuditLog models
     * @param responses Array of organization audit log responses from the API
     * @returns Array of mapped AuditLog instances
     */
    static mapOrganizationAuditLogs(responses: OrganizationAuditLogModel[]): AuditLog[] {
        return responses.map(response => this.mapOrganizationAuditLog(response));
    }

    /**
     * Maps a PropertyAuditLogResponse to the generic AuditLog model
     * @param response The property audit log response from the API
     * @returns A mapped AuditLog instance
     */
    static mapPropertyAuditLog(response: PropertyAuditLogModel): AuditLog {
        return {
            entityId: response.propertyId,
            actingEntityId: response.actingUserId ?? 0,
            actingEntityName: response.actingUserName ?? '',
            action: response.action,
            actionTimestamp: response.actionTimestamp,
            note: response.note ?? undefined,
            entityType: AuditEntityType.Property
        };
    }

    /**
     * Maps an array of PropertyAuditLogResponse to an array of generic AuditLog models
     * @param responses Array of property audit log responses from the API
     * @returns Array of mapped AuditLog instances
     */
    static mapPropertyAuditLogs(responses: PropertyAuditLogModel[]): AuditLog[] {
        return responses.map(response => this.mapPropertyAuditLog(response));
    }

    /**
     * Maps a PropertyChargeScheduleAuditLogModel to the generic AuditLog model
     * @param response The property charge schedule audit log response from the API
     * @returns A mapped AuditLog instance
     */
    static mapPropertyChargeScheduleAuditLog(response: PropertyChargeScheduleAuditLogModel): AuditLog {
        return {
            entityId: response.propertyChargeScheduleId,
            actingEntityId: response.actingUserId ?? 0,
            actingEntityName: response.actingUserName ?? '',
            action: response.action,
            actionTimestamp: response.actionTimestamp,
            note: response.note ?? undefined,
            entityType: AuditEntityType.PropertyChargeSchedule
        };
    }

    /**
     * Maps an array of PropertyChargeScheduleAuditLogModel to an array of generic AuditLog models
     * @param responses Array of property charge schedule audit log responses from the API
     * @returns Array of mapped AuditLog instances
     */
    static mapPropertyChargeScheduleAuditLogs(responses: PropertyChargeScheduleAuditLogModel[]): AuditLog[] {
        return responses.map(response => this.mapPropertyChargeScheduleAuditLog(response));
    }

    /**
     * Maps a PropertyChargeAuditLogModel to the generic AuditLog model
     * @param response The property charge (invoice) audit log response from the API
     * @returns A mapped AuditLog instance
     */
    static mapPropertyChargeAuditLog(response: PropertyChargeAuditLogModel): AuditLog {
        return {
            entityId: response.propertyChargeId,
            actingEntityId: response.actingUserId ?? 0,
            actingEntityName: response.actingUserName ?? '',
            action: response.action,
            actionTimestamp: response.actionTimestamp,
            note: response.note ?? undefined,
            entityType: AuditEntityType.PropertyCharge
        };
    }

    /**
     * Maps an array of PropertyChargeAuditLogModel to an array of generic AuditLog models
     * @param responses Array of property charge (invoice) audit log responses from the API
     * @returns Array of mapped AuditLog instances
     */
    static mapPropertyChargeAuditLogs(responses: PropertyChargeAuditLogModel[]): AuditLog[] {
        return responses.map(response => this.mapPropertyChargeAuditLog(response));
    }

    /**
     * Maps a BudgetAuditLogModel to the generic AuditLog model
     * @param response The budget audit log response from the API
     * @returns A mapped AuditLog instance
     */
    static mapBudgetAuditLog(response: BudgetAuditLogModel): AuditLog {
        return {
            entityId: response.budgetId,
            actingEntityId: response.actingUserId ?? 0,
            actingEntityName: response.actingUserName ?? '',
            action: response.action,
            actionTimestamp: response.actionTimestamp,
            note: response.note ?? undefined,
            entityType: AuditEntityType.Budget
        };
    }

    /**
     * Maps an array of BudgetAuditLogModel to an array of generic AuditLog models
     * @param responses Array of budget audit log responses from the API
     * @returns Array of mapped AuditLog instances
     */
    static mapBudgetAuditLogs(responses: BudgetAuditLogModel[]): AuditLog[] {
        return responses.map(response => this.mapBudgetAuditLog(response));
    }

    /**
     * Maps a DocumentCenterAuditLogModel to the generic AuditLog model
     * @param response The document center audit log response from the API
     * @returns A mapped AuditLog instance
     */
    static mapDocumentCenterAuditLog(response: DocumentCenterAuditLogModel): AuditLog {
        return {
            entityId: response.organizationId,
            actingEntityId: response.actingUserId ?? 0,
            actingEntityName: response.actingUserName ?? '',
            action: response.action,
            actionTimestamp: response.actionTimestamp,
            note: response.note ?? undefined,
            entityType: AuditEntityType.DocumentCenter
        };
    }

    /**
     * Maps an array of DocumentCenterAuditLogModel to an array of generic AuditLog models
     * @param responses Array of document center audit log responses from the API
     * @returns Array of mapped AuditLog instances
     */
    static mapDocumentCenterAuditLogs(responses: DocumentCenterAuditLogModel[]): AuditLog[] {
        return responses.map(response => this.mapDocumentCenterAuditLog(response));
    }

    /**
     * Maps a ChartOfAccountAuditLogModel to the generic AuditLog model
     * @param response The chart of account audit log response from the API
     * @returns A mapped AuditLog instance
     */
    static mapChartOfAccountAuditLog(response: ChartOfAccountAuditLogModel): AuditLog {
        return {
            entityId: response.chartOfAccountId,
            actingEntityId: response.actingUserId ?? 0,
            actingEntityName: response.actingUserName ?? '',
            action: response.action,
            actionTimestamp: response.actionTimestamp,
            note: response.note ?? undefined,
            entityType: AuditEntityType.ChartOfAccount
        };
    }

    /**
     * Maps an array of ChartOfAccountAuditLogModel to an array of generic AuditLog models
     * @param responses Array of chart of account audit log responses from the API
     * @returns Array of mapped AuditLog instances
     */
    static mapChartOfAccountAuditLogs(responses: ChartOfAccountAuditLogModel[]): AuditLog[] {
        return responses.map(response => this.mapChartOfAccountAuditLog(response));
    }

    /**
     * Maps a BankTransactionAuditLogModel to the generic AuditLog model.
     *
     * The log is read per connection, so the connection is the entity it belongs to. The transaction
     * the action was taken on is already named in the action text, which is what the reader sees.
     */
    static mapBankTransactionAuditLog(response: BankTransactionAuditLogModel): AuditLog {
        return {
            entityId: response.bankConnectionId,
            actingEntityId: response.actingUserId ?? 0,
            actingEntityName: response.actingUserName ?? '',
            action: response.action,
            actionTimestamp: response.actionTimestamp,
            note: response.note ?? undefined,
            entityType: AuditEntityType.BankConnection
        };
    }

    static mapBankTransactionAuditLogs(responses: BankTransactionAuditLogModel[]): AuditLog[] {
        return responses.map(response => this.mapBankTransactionAuditLog(response));
    }
}
